import { useState } from 'react';
import { Card, CardHeader, CardContent } from '../core/Card';
import { Button } from '../core/Button';
import { Modal } from '../core/Modal';
import { Input } from '../core/Form';
import { Plus, Edit2, Car, User, Gauge, ArrowLeft } from 'lucide-react';

interface Vehicle {
  id: string;
  licensePlate: string;
  assignedUserId: string | null;
  assignedUserName: string | null;
  mileage: number;
  brand: string;
  model: string;
}

export function VehiclesView() {
  const [showVehicleModal, setShowVehicleModal] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null);
  const [formData, setFormData] = useState<Partial<Vehicle>>({
    licensePlate: '',
    assignedUserId: null,
    mileage: 0,
    brand: '',
    model: '',
  });

  // Mock data
  const [vehicles] = useState<Vehicle[]>([
    {
      id: '1',
      licensePlate: 'B-AB 1234',
      assignedUserId: '1',
      assignedUserName: 'Müller, Thomas',
      mileage: 45280,
      brand: 'VW',
      model: 'Transporter',
    },
    {
      id: '2',
      licensePlate: 'B-CD 5678',
      assignedUserId: '3',
      assignedUserName: 'Weber, Klaus',
      mileage: 32150,
      brand: 'Mercedes',
      model: 'Sprinter',
    },
    {
      id: '3',
      licensePlate: 'B-EF 9012',
      assignedUserId: null,
      assignedUserName: null,
      mileage: 18920,
      brand: 'Ford',
      model: 'Transit',
    },
  ]);

  const users = [
    { id: '1', name: 'Müller, Thomas' },
    { id: '2', name: 'Schmidt, Anna' },
    { id: '3', name: 'Weber, Klaus' },
    { id: '4', name: 'Meyer, Lisa' },
    { id: '5', name: 'Fischer, Marc' },
  ];

  const handleCreateVehicle = () => {
    setEditingVehicle(null);
    setFormData({
      licensePlate: '',
      assignedUserId: null,
      mileage: 0,
      brand: '',
      model: '',
    });
    setShowVehicleModal(true);
  };

  const handleEditVehicle = (vehicle: Vehicle) => {
    setEditingVehicle(vehicle);
    setFormData(vehicle);
    setShowVehicleModal(true);
  };

  const handleSaveVehicle = () => {
    console.log('Saving vehicle:', formData);
    alert(editingVehicle ? 'Fahrzeug aktualisiert' : 'Fahrzeug erstellt');
    setShowVehicleModal(false);
  };

  const handleBackToAdmin = () => {
    const event = new CustomEvent('navigate', {
      detail: { page: 'admin' }
    });
    window.dispatchEvent(event);
  };

  return (
    <div className="bg-[#f5f5f5] min-h-screen p-4 md:p-8">
      <div className="max-w-[1400px] mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-start justify-between mb-4">
            <button
              onClick={handleBackToAdmin}
              className="flex items-center gap-2 text-[#6b6b6b] hover:text-[#0084c7] transition-colors group"
              title="Zurück zum Admin-Bereich"
            >
              <ArrowLeft className="size-5 group-hover:-translate-x-1 transition-transform" />
              <span className="text-[14px] font-medium">Zurück zum Admin-Bereich</span>
            </button>
            <Button
              variant="primary"
              size="md"
              onClick={handleCreateVehicle}
              icon={<Plus className="size-4" />}
            >
              Neues Fahrzeug
            </Button>
          </div>
          <h1 className="text-[#1c1c1c] text-[28px] md:text-[32px] font-medium leading-tight mb-2">
            Fahrzeuge
          </h1>
          <p className="text-[#6b6b6b] text-[16px]">
            Firmenwagen verwalten
          </p>
        </div>

        {/* Vehicles Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {vehicles.map((vehicle) => (
            <Card key={vehicle.id}>
              <CardContent>
                <div className="space-y-4">
                  {/* License Plate */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="size-12 bg-[#0084c7]/10 rounded-full flex items-center justify-center">
                        <Car className="size-6 text-[#0084c7]" />
                      </div>
                      <div>
                        <div className="text-[#1c1c1c] text-[18px] font-bold font-mono">
                          {vehicle.licensePlate}
                        </div>
                        <div className="text-[#6b6b6b] text-[13px]">
                          {vehicle.brand} {vehicle.model}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="h-px bg-[#e9e9e9]" />

                  {/* Details */}
                  <div className="space-y-3">
                    {/* Assigned User */}
                    <div className="flex items-center gap-2">
                      <User className="size-4 text-[#6b6b6b]" />
                      <span className="text-[#6b6b6b] text-[13px]">Zugewiesen:</span>
                      <span className="text-[#1c1c1c] text-[14px] font-medium">
                        {vehicle.assignedUserName || 'Nicht zugewiesen'}
                      </span>
                    </div>

                    {/* Mileage */}
                    <div className="flex items-center gap-2">
                      <Gauge className="size-4 text-[#6b6b6b]" />
                      <span className="text-[#6b6b6b] text-[13px]">Kilometerstand:</span>
                      <span className="text-[#1c1c1c] text-[14px] font-medium font-mono">
                        {vehicle.mileage.toLocaleString()} km
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <Button
                    variant="secondary"
                    size="sm"
                    fullWidth
                    onClick={() => handleEditVehicle(vehicle)}
                    icon={<Edit2 className="size-4" />}
                  >
                    Bearbeiten
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Empty State */}
        {vehicles.length === 0 && (
          <Card>
            <CardContent>
              <div className="py-12 text-center">
                <Car className="size-12 text-[#6b6b6b] mx-auto mb-4" />
                <p className="text-[#6b6b6b] text-[14px]">
                  Noch keine Fahrzeuge angelegt
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Vehicle Modal */}
        {showVehicleModal && (
          <Modal
            isOpen={showVehicleModal}
            onClose={() => setShowVehicleModal(false)}
            title={editingVehicle ? 'Fahrzeug bearbeiten' : 'Neues Fahrzeug'}
            size="md"
          >
            <div className="space-y-4">
              <Input
                label="Kennzeichen"
                value={formData.licensePlate || ''}
                onChange={(e) => setFormData({ ...formData, licensePlate: e.target.value })}
                placeholder="B-AB 1234"
                required
              />

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Marke"
                  value={formData.brand || ''}
                  onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                  placeholder="VW"
                  required
                />
                <Input
                  label="Modell"
                  value={formData.model || ''}
                  onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                  placeholder="Transporter"
                  required
                />
              </div>

              <Input
                label="Kilometerstand"
                type="number"
                value={formData.mileage || 0}
                onChange={(e) => setFormData({ ...formData, mileage: parseInt(e.target.value) })}
                min={0}
                required
              />

              <div>
                <label className="block text-[#1c1c1c] text-[14px] font-medium mb-2">
                  Zugewiesener Mitarbeiter
                </label>
                <select
                  value={formData.assignedUserId || ''}
                  onChange={(e) => {
                    const userId = e.target.value || null;
                    const userName = userId ? users.find(u => u.id === userId)?.name || null : null;
                    setFormData({ 
                      ...formData, 
                      assignedUserId: userId,
                      assignedUserName: userName,
                    });
                  }}
                  className="w-full px-4 h-[44px] rounded-[10px] border border-[#e9e9e9] bg-white text-[#1c1c1c] text-[14px] focus:outline-none focus:border-[#0084c7] transition-colors"
                >
                  <option value="">Nicht zugewiesen</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex gap-3 pt-4 border-t border-[#e9e9e9]">
                <Button
                  variant="secondary"
                  size="md"
                  fullWidth
                  onClick={() => setShowVehicleModal(false)}
                >
                  Abbrechen
                </Button>
                <Button
                  variant="primary"
                  size="md"
                  fullWidth
                  onClick={handleSaveVehicle}
                >
                  {editingVehicle ? 'Speichern' : 'Erstellen'}
                </Button>
              </div>
            </div>
          </Modal>
        )}
      </div>
    </div>
  );
}